import { Injectable } from '@angular/core';
import {
  Actions,
  ofType,
  createEffect,
  ROOT_EFFECTS_INIT,
} from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, first, map, switchMap, tap } from 'rxjs/operators';
import * as UserActions from './user.actions';
import { API } from '@app/core/models';
import { UsersHttpService } from '@app/core/async-services/http/versioned/users.http';
import { TelemetryService } from '@app/core/services/telemetry/telemetry.service';
import { PermissionService } from '@app/core/services';
import { TelemetryData } from '@app/core/services/telemetry/telemetry.interface';
import { Permission } from '@app/core/models/permissions.interface';

@Injectable()
export class Effects {
  getUser$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.getUser),
      switchMap(() =>
        this._usersHttpService.getCurrentLoggedInUserInformation().pipe(
          map((response) =>
            UserActions.getUserSuccess({ user: response.payload }),
          ),
          catchError((response: API.Response) =>
            of(UserActions.getUserFailure({ message: response.message })),
          ),
        ),
      ),
    ),
  );

  initializeTelemetry$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(ROOT_EFFECTS_INIT),
        tap(() => {
          this.telemetryService.initialize();
        }),
      ),
    { dispatch: false },
  );

  identifyUser$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(UserActions.getUserSuccess),
        switchMap(({ user }) =>
          this.permissionsService.getPermissionList().pipe(
            first(),
            map((permissions) => ({ user, permissions })),
          ),
        ),
        tap(({ user, permissions }) => {
          const telemetryData: TelemetryData = {
            username: user.userName,
            firstName: user.name?.first,
            lastName: user.name?.last,
            email: user.emailAddress,
            userType: user.userType,
            jobTitle: user.jobTitle,
            isAccountAdmin: permissions.includes(Permission.UserAccountAdmin),
            isHotelAdmin: permissions.includes(Permission.UserHotelAdmin),
            hasWageAccess: user.canSeeWages,
            isDepartmentFiltered: user.hasDepartmentFilters,
            hotelSuspensionStatus: user.hotelSuspensionStatus,
            hotelSuspensionList: user.hotelSuspensionList,
            permissions: permissions,
            accountName: user.accounts?.[0].accountName,
          };
          this.telemetryService.identify(telemetryData);
        }),
      ),
    { dispatch: false },
  );

  notificationPreferences$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.getNotificationPreferences),
      switchMap(() => {
        return this._usersHttpService.getCommunicationsPreferences().pipe(
          map((response) =>
            UserActions.getNotificationPreferencesSuccess({
              preferences: response.payload,
            }),
          ),
          catchError((response: API.Response) =>
            of(
              UserActions.getNotificationPreferencesFailure({
                message: response.message,
              }),
            ),
          ),
        );
      }),
    ),
  );

  updateNotificationPreference$ = createEffect(() =>
    this._actions$.pipe(
      ofType(UserActions.updateNotificationPreference),
      switchMap(({ preference }) => {
        return this._usersHttpService
          .updateNotificationPreference(preference)
          .pipe(
            map(() =>
              UserActions.updateNotificationPreferenceSuccess({ preference }),
            ),
            catchError((response: API.Response) =>
              of(
                UserActions.updateNotificationPreferenceFailure({
                  message: response.message,
                }),
              ),
            ),
          );
      }),
    ),
  );

  constructor(
    private _actions$: Actions,
    private _usersHttpService: UsersHttpService,
    private telemetryService: TelemetryService,
    private permissionsService: PermissionService,
  ) {}
}
